import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import moment from "moment";
import type { UUID } from "@/Shared/types";
import ShipmentRow from "@/Shared/Shipment/CustomComponents/ShipmentRow";
import { ViewShipmentsFilterBar } from "../Bars/ViewShipmentsFilterBar";
import type { FilterShipmentsDescription } from "@/Shared/ShipmentFilter/Dialogs/Internal/FilterShipmentsDialogBody";
import { SortShipmentOptions } from "@/Shared/Shipment/SortShipmentOptions";
import { EmptyState } from "@freightsimple/ui-core/Display/EmptyState";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";

// Four of the sorts are by date, and for those the row's last column shows the
// date that was sorted on instead of its usual content — otherwise the list
// would be ordered by something the customer can't see. The other sorts leave
// the column alone.
function generateLastColumnOverrideLabel(
  sort: SortShipmentOptions,
): string | undefined {
  if (sort === SortShipmentOptions.BookingDate) {
    return "Booking Date";
  }

  if (sort === SortShipmentOptions.DeliveryDate) {
    return "Delivery Date";
  }

  if (sort === SortShipmentOptions.EstimatedDeliveryDate) {
    return "Earliest Delivery Date";
  }

  if (sort === SortShipmentOptions.PickupDate) {
    return "Pickup Date";
  }

  return undefined;
}

function generateLastColumnOverrideDescription(
  shipment: Shipment,
  sort: SortShipmentOptions,
): string | undefined {
  function handleDate(dateString: string | undefined): string {
    if (dateString === undefined) {
      return "Unknown";
    }

    return moment(dateString).format("Do MMMM YYYY");
  }

  if (sort === SortShipmentOptions.BookingDate) {
    return handleDate(shipment.bookedAt);
  }

  if (sort === SortShipmentOptions.DeliveryDate) {
    return handleDate(shipment.actualDeliveryDate);
  }

  if (sort === SortShipmentOptions.EstimatedDeliveryDate) {
    return handleDate(shipment.expectedDeliveryDate);
  }

  if (sort === SortShipmentOptions.PickupDate) {
    return handleDate(shipment.pickupDate);
  }

  return undefined;
}

interface DashboardRowForShipmentProps {
  shipment: Shipment;
  onClick: () => void;
  sort: SortShipmentOptions;
  bookedByName: string | undefined;
}

function DashboardRowForShipment_(props: DashboardRowForShipmentProps) {
  const { shipment, onClick, sort, bookedByName } = props;
  const quote = shipment.selectedQuote;

  return (
    <ShipmentRow
      key={shipment.shipmentId}
      lineItems={shipment.lineItems}
      bookedByName={bookedByName}
      shipmentState={shipment.shipmentState}
      pickupLocation={shipment.pickupLocation}
      deliveryLocation={shipment.deliveryLocation}
      pickupDate={shipment.pickupDate}
      pickupHours={shipment.pickupLocation.hours}
      appointmentDate={shipment.appointmentDate}
      expectedDeliveryDate={shipment.expectedDeliveryDate}
      expectedDeliveryHours={shipment.expectedDeliveryHours}
      actualDeliveryDate={shipment.actualDeliveryDate}
      actualDeliveryTime={shipment.actualDeliveryTime}
      carrierIdentifier={quote?.carrierIdentifier}
      onClick={onClick}
      pickupReference={shipment.pickupReferenceNumber}
      deliveryReference={shipment.deliveryReferenceNumber}
      overrideLastColumnLabel={generateLastColumnOverrideLabel(sort)}
      overrideLastColumnDescription={generateLastColumnOverrideDescription(
        shipment,
        sort,
      )}
      manualQuotingOpen={false}
      freightClaims={shipment.hasFreightClaims}
    />
  );
}

interface ViewShipmentsAllShipmentsBodyProps {
  onFilter: () => Promise<void>;
  onSortChange: (sort: SortShipmentOptions) => void;
  filter: FilterShipmentsDescription;
  sort: SortShipmentOptions;
  setFilter: (newFilter: FilterShipmentsDescription) => void;
  filteredShipments: Array<Shipment>;
  onClick: (shipmentId: UUID) => void;
  bookerNameFor: (bookedBy: string | undefined) => string | undefined;
}

/**
 * The filter bar and the list of shipment rows beneath it.
 *
 * The "nothing matches" message is separate from the screen's own empty state:
 * this one means the filters excluded everything, that one means the customer
 * has never booked a shipment.
 */
export function ViewShipmentsAllShipmentsBody(
  props: ViewShipmentsAllShipmentsBodyProps,
) {
  const {
    onFilter,
    onSortChange,
    filter,
    sort,
    setFilter,
    filteredShipments,
    onClick,
    bookerNameFor,
  } = props;

  return (
    <Stack gap={4}>
      <ViewShipmentsFilterBar
        onFilter={onFilter}
        onSortChange={onSortChange}
        filter={filter}
        sort={sort}
        setFilter={setFilter}
      />
      {/* The rows butt together, so the inner Stack carries no gap — only the
          bar is separated from the list. */}
      <Stack gap={0}>
        {/* Was absolutely positioned 180px down a full-viewport-wide box; the
            message now sits in the list's own flow, which is what EmptyState
            is for. */}
        {filteredShipments.length === 0 && (
          <EmptyState title="No shipments match the applied filters" />
        )}
        {filteredShipments.map((s) => {
          return (
            <DashboardRowForShipment_
              key={s.shipmentId}
              shipment={s}
              onClick={function () {
                onClick(s.shipmentId);
              }}
              sort={sort}
              bookedByName={bookerNameFor(s.bookedBy)}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}
